import React from 'react'
import { formatPhoneNumber } from 'utils/phoneNumber'
import { makeStyles } from '@material-ui/styles'
import Box from '@material-ui/core/Box'
import TableCell from '@material-ui/core/TableCell'
import TableRow from '@material-ui/core/TableRow'
import Typography from '@material-ui/core/Typography'
import ListItemIcon from '@material-ui/core/ListItemIcon'
import Avatar from '@material-ui/core/Avatar'
import MenuItem from '@material-ui/core/MenuItem'
import ListItemText from '@material-ui/core/ListItemText'
import ActionsMenu from 'components/actions-menu'
import PhoneOutlined from 'components/material-symbol/icons/Phone'
import EmailOutlinedIcon from 'components/material-symbol/icons/Email'
import MoreHoriz from 'components/material-symbol/icons/MoreHoriz'
import { useIsTablet } from 'lib/hooks'
import { getInitials } from 'lib/text-display'

const useStyles = makeStyles(theme => ({
  info: {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    minWidth: 0,
  },
  nameCell: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    minWidth: 0,
  },
  studentAvatar: {
    height: '36px',
    width: '36px',
    flexShrink: 0,
    backgroundColor: '#EBF0F2',
    color: '#172024',
    fontSize: '13px',
    fontWeight: 500,
  },
  text: {
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '20px',
    letterSpacing: '0.15px',
    textAlign: 'left',
  },
  actions: {
    textAlign: 'right',
    width: 1,
    whiteSpace: 'nowrap',
  },
  textSecondary: {
    color: theme.palette.text.secondary,
    fontSize: '12px',
    lineHeight: '16px',
  },
  sessionCell: {
    minWidth: 0,
  },
  sessionCellContent: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    minWidth: 0,
  },
  sessionDateBadge: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: 40,
    height: 40,
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.common.white,
  },
  sessionDateMonth: {
    fontSize: '10px',
    fontWeight: 500,
    lineHeight: '12px',
    letterSpacing: '0.4px',
    color: '#6B7C87',
  },
  sessionDateDay: {
    fontSize: '16px',
    fontWeight: 600,
    lineHeight: '20px',
    color: '#172024',
  },
  sessionText: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    overflow: 'hidden',
  },
  sessionTime: {
    fontSize: '12px',
    fontWeight: 400,
    lineHeight: '16px',
    color: '#6B7C87',
  },
  ageGroupChip: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 9999,
    padding: '2px 8px',
    fontSize: '12px',
    fontWeight: 500,
    lineHeight: '16px',
    whiteSpace: 'nowrap',
  },
  ageGroupChipJunior: {
    backgroundColor: '#EBF0F2',
    color: '#172024',
  },
  ageGroupChipAdult: {
    backgroundColor: '#E0EAFF',
    color: '#001C57',
  },
  plainActionButton: {
    padding: 0,
    minWidth: 0,
    width: 24,
    height: 24,
    borderRadius: 4,
    color: '#6B7C87',
    transition: theme.transitions.create(['background-color', 'color'], {
      duration: theme.transitions.duration.short,
    }),
    '&:hover': {
      backgroundColor: 'rgba(0, 35, 75, 0.04)',
      color: '#172024',
    },
    '& .MuiSvgIcon-root': {
      width: 20,
      height: 20,
      fontSize: 20,
    },
  },
  menuIcon: {
    minWidth: 36,
    color: 'rgba(0, 0, 0, 0.54)',
  },
}))

const StudentItemView = ({ student }) => {
  const classes = useStyles()
  const isTablet = useIsTablet()
  const ageGroupLabel = student.isMinor ? 'Junior' : 'Adult'
  const hasSession = Boolean(student?.upcomingSession?.startDateTime)
  const coachNames = (student.coaches || [])
    .map(coach => coach?.name)
    .filter(Boolean)
    .join(', ')
  const phoneNumber = student.representatives?.phoneNumber
  const email = student.representatives?.email
  const hasContactActions = Boolean(phoneNumber || email)

  return (
    <TableRow data-testid={`academy-student-row-${student.id}`}>
      <TableCell className={classes.info}>
        <Box
          className={classes.nameCell}
          data-testid={`academy-student-name-${student.id}`}
        >
          <Avatar className={classes.studentAvatar}>
            {getInitials(`${student.firstName} ${student.lastName}`)}
          </Avatar>
          <Box className={classes.info}>
            <Typography className={classes.text} noWrap>
              {student.firstName} {student.lastName}
            </Typography>
          </Box>
        </Box>
      </TableCell>
      <TableCell>
        <span
          className={`${classes.ageGroupChip} ${
            student.isMinor
              ? classes.ageGroupChipJunior
              : classes.ageGroupChipAdult
          }`}
        >
          {ageGroupLabel}
        </span>
      </TableCell>
      {!isTablet && (
        <TableCell className={classes.sessionCell}>
          {hasSession ? (
            <Box className={classes.sessionCellContent}>
              <Box className={classes.sessionDateBadge} aria-hidden="true">
                <Typography className={classes.sessionDateMonth}>
                  {student.upcomingSession.sessionMonth}
                </Typography>
                <Typography className={classes.sessionDateDay}>
                  {student.upcomingSession.sessionDay}
                </Typography>
              </Box>
              <Box className={classes.sessionText}>
                <Typography className={classes.text} noWrap>
                  {student.upcomingSession.title}
                </Typography>
                <Typography className={classes.sessionTime} noWrap>
                  {student.upcomingSession.sessionTime}
                </Typography>
              </Box>
            </Box>
          ) : (
            <Typography className={classes.textSecondary}>
              No upcoming sessions
            </Typography>
          )}
        </TableCell>
      )}
      {!isTablet && (
        <TableCell className={classes.info}>
          <Typography className={classes.text} noWrap>
            {coachNames || '—'}
          </Typography>
        </TableCell>
      )}
      <TableCell className={classes.actions} onClick={e => e.stopPropagation()}>
        {hasContactActions && (
          <ActionsMenu
            ariaId={`academy-student-actions-${student.id}`}
            aria-label={`Actions for ${student.firstName} ${student.lastName}`}
            data-testid={`academy-student-actions-${student.id}`}
            buttonContent={<MoreHoriz fontSize="small" />}
            className={classes.plainActionButton}
            color="default"
          >
            {phoneNumber && (
              <MenuItem component="a" href={`tel:${phoneNumber}`}>
                <ListItemIcon className={classes.menuIcon}>
                  <PhoneOutlined fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={formatPhoneNumber(phoneNumber)} />
              </MenuItem>
            )}
            {email && (
              <MenuItem component="a" href={`mailto:${email}`}>
                <ListItemIcon className={classes.menuIcon}>
                  <EmailOutlinedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={email} />
              </MenuItem>
            )}
          </ActionsMenu>
        )}
      </TableCell>
    </TableRow>
  )
}

export default StudentItemView
